import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { tenantApi, SupportChatResponse } from "@/lib/api/client";
import { contactApi, type ContactConversation, type ContactGroup } from "@/lib/api/contact";
import { usePortalEvent } from "@/lib/portal-socket";
import { useAuthStore } from "@/lib/auth";
import { Badge, Button, TextInput, Text, Box, Group, Stack, Flex, Loader, Menu, SegmentedControl } from "@mantine/core";
import { ArrowLeft, Send, Plus, Archive, XCircle, Headset } from "lucide-react";
import { RoutedToBadges } from "@/components/support/routed-to-badges";

// On a ticket the member chooses each message's recipient: "Agent" (the human
// thread, no AI reply) or "Assistant" (the AI replies). The AI must NOT
// auto-reply on a ticket — it only replies when the member explicitly picks
// "Assistant".
type Recipient = "agent" | "assistant";

interface ChatMessage {
  role: "user" | "assistant" | "staff";
  content: string;
  /** Source of truth for "the AI wrote this". */
  is_ai?: boolean;
  /** Attribution: "AI" when is_ai; else the human author's name. */
  author_name?: string;
  /** Human author's user id — tells "my own message" from a colleague's once
   *  this chat has become a multi-participant ticket. */
  author_user_id?: number | null;
}

function historyToMessages(
  history: { role: string; content: string; is_ai?: boolean; author_name?: string; author_user_id?: number | null }[],
): ChatMessage[] {
  return history.map((m) => ({
    role: m.role as ChatMessage["role"],
    content: m.content,
    is_ai: m.is_ai,
    author_name: m.author_name,
    author_user_id: m.author_user_id,
  }));
}

const SUGGESTED = ["Find a meeting room", "Search the Office Guide", "Who are my colleagues?"];

export default function SupportChatPage() {
  const { conversationId: paramId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | undefined>(
    paramId ? parseInt(paramId, 10) : undefined,
  );
  const [conversationStatus, setConversationStatus] = useState<string>("open");
  const [isTicket, setIsTicket] = useState(false);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [recipient, setRecipient] = useState<Recipient>("agent");
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [escalating, setEscalating] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);

  // Silent refetch (no "Thinking…" spinner) — used by the realtime push so a
  // staff reply after an escalation shows up live without a manual reload.
  function refreshConversation(id: number) {
    tenantApi.getSupportConversation(id)
      .then((res) => {
        setMessages(historyToMessages(res.data.history));
        setConversationStatus(res.data.status);
        setIsTicket(res.data.is_ticket);
        setGroupIds(res.data.group_ids);
      })
      .catch(() => {});
  }

  // Load existing conversation if ID provided
  useEffect(() => {
    if (conversationId) {
      setIsLoading(true);
      tenantApi.getSupportConversation(conversationId)
        .then((res) => {
          setMessages(historyToMessages(res.data.history));
          setConversationStatus(res.data.status);
          setIsTicket(res.data.is_ticket);
          setGroupIds(res.data.group_ids);
        })
        .catch(() => {
          setMessages([{ role: "assistant", content: "Failed to load conversation." }]);
        })
        .finally(() => setIsLoading(false));
    } else {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, []);

  // Opening the thread IS reading it — clear its unread so the Support badge
  // drops without the member hunting for a button. Its own effect (rather than
  // riding the mount-only loader above) so it re-runs if the route switches to
  // another conversation. Best-effort: a failure here must never block
  // rendering the thread.
  useEffect(() => {
    if (conversationId === undefined) return;
    tenantApi
      .markSupportConversationRead(conversationId)
      .then(() => queryClient.invalidateQueries({ queryKey: ["support-conversations"] }))
      .catch(() => undefined);
  }, [conversationId, queryClient]);

  // Live updates: staff replies and escalations broadcast "support_message" to
  // the member. The event now carries the full conversation (same shape as
  // GET /support/conversations/:id/) — apply it directly to skip the slow GET;
  // fall back to refreshConversation only when the payload is absent.
  usePortalEvent("support_message", (e) => {
    // Apply even while a send is in flight: the pushed payload is the full
    // authoritative history, so it can't drop the in-flight message — and
    // gating on isLoading would silently lose a staff reply that lands mid-send.
    if (conversationId === undefined) return;
    if (e.conversation_id && e.conversation_id !== conversationId) return;
    const pushed = e.conversation as ContactConversation | undefined;
    if (pushed) {
      setMessages(historyToMessages(pushed.history));
      setConversationStatus(pushed.status);
      setIsTicket(pushed.is_ticket);
      setGroupIds(pushed.group_ids);
    } else {
      refreshConversation(conversationId);
    }
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function sendMessage(text?: string) {
    const msg = (text ?? input).trim();
    if (!msg || isLoading) return;

    setInput("");
    // Stamp the optimistic bubble as mine so it renders right-aligned (blue)
    // immediately — the server echo carries the same author_user_id.
    setMessages((prev) => [...prev, { role: "user", content: msg, author_user_id: currentUserId }]);
    setIsLoading(true);

    try {
      // On a ticket the member picks the recipient. "Agent" posts to the human
      // thread (no AI reply); "Assistant" runs the AI. A pure AI chat always
      // runs the AI. This keeps the AI from auto-replying on a ticket.
      if (isTicket && recipient === "agent" && conversationId !== undefined) {
        await contactApi.postMessage(conversationId, msg);
        refreshConversation(conversationId);
      } else {
        const res = await tenantApi.supportChat(msg, conversationId);
        const data: SupportChatResponse = res.data;
        if (!conversationId) {
          queryClient.invalidateQueries({ queryKey: ["support-conversations"] });
        }
        setConversationId(data.conversation_id);
        // Update URL without reloading
        if (!paramId) {
          navigate(`/support/${data.conversation_id}`, { replace: true });
        }
        setMessages(historyToMessages(data.history));
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong. Please try again." },
      ]);
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  async function archiveConversation() {
    await tenantApi.patchSupportConversation(conversationId!, "archived");
    navigate(-1);
  }

  async function closeConversation() {
    await tenantApi.patchSupportConversation(conversationId!, "closed");
    navigate(-1);
  }

  async function loadGroups() {
    if (groups.length > 0) return;
    try {
      const res = await contactApi.getContactGroups();
      setGroups(res.data);
    } catch {
      // Best-effort: leave the menu empty if groups can't be loaded.
    }
  }

  async function escalateTo(group: ContactGroup) {
    if (conversationId === undefined || escalating) return;
    setEscalating(true);
    try {
      await contactApi.escalate(conversationId, [group.id]);
      // It's now a ticket: hide "Talk to a human", show the recipient toggle,
      // and surface the routed group. Default new messages to the human Agent.
      setIsTicket(true);
      setGroupIds((prev) => (prev.includes(group.id) ? prev : [...prev, group.id]));
      setRecipient("agent");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `I've passed this conversation on to **${group.name}**. A human will follow up with you here.`,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, I couldn't reach a human right now. Please try again." },
      ]);
    } finally {
      setEscalating(false);
    }
  }

  return (
    <Flex direction="column" h="calc(100vh - 56px - 48px)">
      {/* Header */}
      <Group gap="xs" px="md" py="sm" style={{ borderBottom: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}>
        <Button variant="subtle" size="compact-md" onClick={() => navigate(-1)} aria-label="Back" p={4}>
          <ArrowLeft style={{ height: 16, width: 16 }} />
        </Button>
        <Text fw={600}>Support</Text>
        <Box flex={1} />
        {conversationId !== undefined && conversationStatus === "open" && (
          <>
            {/* "Talk to a human" only makes sense on a pure AI chat. Once it's a
                ticket it's already with humans, so hide it. */}
            {!isTicket && (
              <Menu shadow="md" width={220} onOpen={loadGroups}>
                <Menu.Target>
                  <Button
                    variant="light"
                    size="xs"
                    loading={escalating}
                    leftSection={<Headset style={{ height: 16, width: 16 }} />}
                  >
                    Talk to a human
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Send this chat to</Menu.Label>
                  {groups.length === 0 ? (
                    <Menu.Item disabled>No groups available</Menu.Item>
                  ) : (
                    groups.map((g) => (
                      <Menu.Item key={g.id} onClick={() => escalateTo(g)}>
                        {g.name}
                      </Menu.Item>
                    ))
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
            <Button
              variant="outline"
              size="xs"
              leftSection={<Archive style={{ height: 16, width: 16 }} />}
              onClick={archiveConversation}
            >
              Archive
            </Button>
            <Button
              variant="outline"
              size="xs"
              leftSection={<XCircle style={{ height: 16, width: 16 }} />}
              onClick={closeConversation}
            >
              Close
            </Button>
          </>
        )}
        <Button
          variant="outline"
          size="xs"
          leftSection={<Plus style={{ height: 16, width: 16 }} />}
          onClick={() => { setMessages([]); setConversationId(undefined); navigate("/support/new", { replace: true }); }}
        >
          New chat
        </Button>
      </Group>

      {/* Routed-group line: only shown once the chat is a ticket with groups. */}
      {isTicket && groupIds.length > 0 && (
        <Box
          px="md"
          py="xs"
          style={{ borderBottom: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}
        >
          <RoutedToBadges groupIds={groupIds} />
        </Box>
      )}

      {/* Messages */}
      <Box style={{ flex: 1, overflowY: "auto" }} p="md">
        <Stack gap="md">
          {messages.length === 0 && !isLoading && (
            <Flex direction="column" align="center" justify="center" style={{ height: "100%" }} gap="md">
              <Text fz="sm" c="dimmed">What can I help you with?</Text>
              <Stack gap="xs" w="100%" maw={384}>
                {SUGGESTED.map((prompt) => (
                  <Button key={prompt} variant="outline" size="sm" justify="flex-start" onClick={() => sendMessage(prompt)}>
                    {prompt}
                  </Button>
                ))}
              </Stack>
            </Flex>
          )}

          {messages.map((msg, i) => {
            // Only your OWN messages sit on the right. A colleague on a ticket is
            // also role "user" — key off the author id so theirs shows on the
            // left with their name. Member-typed text is plain (never markdown).
            const isMemberAuthored = msg.role === "user";
            const isMine =
              isMemberAuthored && msg.author_user_id != null && msg.author_user_id === currentUserId;
            const isStaff = !msg.is_ai && msg.role === "staff";
            return (
              <Flex key={i} direction="column" align={isMine ? "flex-end" : "flex-start"} gap={4}>
                {!isMine && (msg.is_ai || msg.author_name) && (
                  <Group gap={6} px={4} wrap="nowrap">
                    {/* Attribution: is_ai → "AI"; staff → author + "Staff" tag;
                        else the author's name. */}
                    <Text fz={11} c="dimmed">
                      {msg.is_ai ? "AI" : msg.author_name}
                    </Text>
                    {msg.is_ai && (
                      <Badge size="xs" variant="light" color="gray">
                        AI
                      </Badge>
                    )}
                    {isStaff && (
                      <Badge size="xs" variant="light" color="green">
                        Staff
                      </Badge>
                    )}
                  </Group>
                )}
                <Box
                  style={{
                    borderRadius: 16,
                    padding: "10px 16px",
                    fontSize: 14,
                    maxWidth: "75%",
                    // Dark brand shade (not "filled") so white text clears WCAG
                    // 2.1 AA / EN 301 549 (EAA) for any customer brand colour.
                    backgroundColor: isMine
                      ? "var(--mantine-color-brand-8)"
                      : "var(--mantine-color-default-hover)",
                    color: isMine
                      ? "var(--mantine-color-white)"
                      : "var(--mantine-color-text)",
                  }}
                >
                  {!isMemberAuthored ? (
                    <Box fz="sm" maw="none" style={{ lineHeight: 1.6, overflow: "hidden" }}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{ img: ({ node, ...props }) => <img {...props} style={{ maxWidth: "100%", height: "auto", borderRadius: 4 }} /> }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </Box>
                  ) : (
                    msg.content
                  )}
                </Box>
              </Flex>
            );
          })}

          {isLoading && (
            <Flex justify="flex-start">
              <Group
                gap="xs"
                style={{
                  borderRadius: 16,
                  padding: "10px 16px",
                  backgroundColor: "var(--mantine-color-default-hover)",
                }}
              >
                <Loader size={16} />
                <Text fz="sm" c="dimmed">Thinking...</Text>
              </Group>
            </Flex>
          )}

          <div ref={bottomRef} />
        </Stack>
      </Box>

      {/* Input */}
      <Group gap="xs" p="md" style={{ borderTop: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}>
        {isTicket && (
          <SegmentedControl
            size="xs"
            value={recipient}
            onChange={(v) => setRecipient(v as Recipient)}
            disabled={isLoading}
            data={[
              { label: "Agent", value: "agent" },
              { label: "Assistant", value: "assistant" },
            ]}
          />
        )}
        <TextInput
          ref={inputRef}
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          disabled={isLoading}
          flex={1}
        />
        <Button size="sm" onClick={() => sendMessage()} disabled={isLoading || !input.trim()} p={8}>
          {isLoading ? <Loader size={16} /> : <Send style={{ height: 16, width: 16 }} />}
        </Button>
      </Group>
    </Flex>
  );
}
